import type {
  AddDetailReportTypeFormValues,
  AddReportTypeFormValues,
  ExpandedRowProps
} from './types'
import type { ReportConfig } from '../../../../domain/cr-report-type.enum'
import type { FormValues, NotificationRecipient } from '../types'
import type { DefaultOptionType } from 'antd/es/select'

import {
  CalendarOutlined,
  CaretRightOutlined,
  DeleteOutlined,
  EllipsisOutlined,
  InfoCircleOutlined,
  PlusOutlined
} from '@ant-design/icons'
import { v4 as uuid } from '@lukeed/uuid'
import {
  Button,
  Checkbox,
  Col,
  Collapse,
  DatePicker,
  Dropdown,
  Form,
  Input,
  Modal,
  notification,
  Radio,
  Row,
  Select,
  Space,
  Switch,
  Tag,
  Tooltip
} from 'antd'
import classNames from 'classnames'
import { useEffect, useMemo, useState } from 'react'

import {
  CRReportType,
  mapper as crReportTypeNameMapper,
  levelMapper
} from '../../../../domain/cr-report-type.enum'
import {
  getReportConfigLabel,
  humanJoin,
  humanReadableSchedule,
  isEmptySchedule,
  pluralize
} from '../functions'
import { ScheduleDropdown } from '../schedule-dropdown'

export function ExpandedRow({
  form,
  check,
  isDirty,
  locationKey,
  availableCoverageCategories,
  isSubmitting,
  onSave,
  onCancel
}: ExpandedRowProps): JSX.Element {
  const schedule = Form.useWatch('schedule', form)
  const reports = Form.useWatch('reports', form) as ReportConfig[] | undefined
  const network = Form.useWatch('network', form)

  const recipients = Form.useWatch('notificationRecipients', form) as
    | NotificationRecipient[]
    | undefined
  const networkRecipients = Form.useWatch('networkNotificationRecipients', form) as
    | NotificationRecipient[]
    | undefined
  const normalizedRecipients = useMemo(
    () => recipients?.filter(recipient => !!recipient.id) ?? [],
    [recipients]
  )
  const networkNormalizedRecipients = useMemo(
    () => networkRecipients?.filter(recipient => !!recipient.id) ?? [],
    [networkRecipients]
  )

  const releaseType = Form.useWatch('releaseType', form)
  const coverageCategories = Form.useWatch('coverageCategories', form) ?? []

  const paidThrough =
    availableCoverageCategories
      ?.filter(category => !!category.paid_through)
      .filter(category => coverageCategories.includes(category.id)) ?? []

  const specialCaseCoverageCategories = coverageCategories
    .map(id => availableCoverageCategories?.find(c => c.id === id)?.name ?? '')
    .filter(category => ['RXD', 'PBM'].includes(category))

  const { setFieldValue } = form

  const [addReportTypeForm, setAddReportTypeForm] = useState<AddReportTypeFormValues>({})

  const selectedReportConfigHasAlreadyBeenAdded =
    reports?.some(
      config =>
        (config.type === CRReportType.Detail &&
          addReportTypeForm.type === CRReportType.Detail &&
          config.options.includeDiagnosisDetails ===
            addReportTypeForm.options.includeDiagnosisDetails &&
          config.options.includePHI === addReportTypeForm.options.includePHI) ||
        (config.type !== CRReportType.Detail && addReportTypeForm.type === config.type)
    ) ?? false

  const addReportTypeOptions = useMemo<DefaultOptionType[]>(() => {
    const options: DefaultOptionType[] = []

    const hasAllPossibleDetailReports =
      Array.isArray(reports) &&
      reports.some(
        config =>
          config.type === CRReportType.Detail &&
          config.options.includeDiagnosisDetails &&
          config.options.includePHI
      ) &&
      reports.some(
        config =>
          config.type === CRReportType.Detail &&
          !config.options.includeDiagnosisDetails &&
          config.options.includePHI
      ) &&
      reports.some(
        config =>
          config.type === CRReportType.Detail &&
          config.options.includeDiagnosisDetails &&
          !config.options.includePHI
      ) &&
      reports.some(
        config =>
          config.type === CRReportType.Detail &&
          !config.options.includeDiagnosisDetails &&
          !config.options.includePHI
      )

    if (!hasAllPossibleDetailReports) {
      options.push({
        label: crReportTypeNameMapper[CRReportType.Detail],
        value: CRReportType.Detail
      })
    }

    for (const type of Object.values(CRReportType)) {
      if (type === CRReportType.Detail) {
        continue
      }

      if (!reports?.some(config => config.type === type)) {
        options.push({
          label: crReportTypeNameMapper[type],
          value: type
        })
      }
    }

    return options.sort((a, b) =>
      typeof a.label === 'string' && typeof b.label === 'string'
        ? a.label.localeCompare(b.label)
        : 0
    )
  }, [reports])

  useEffect(() => {
    for (const recipient of normalizedRecipients) {
      if (
        recipient.attachments.some(reportId => !reports?.some(report => report.id === reportId))
      ) {
        setFieldValue(
          'notificationRecipients',
          normalizedRecipients.map(recipient => ({
            ...recipient,
            attachments:
              recipient.attachments.filter(reportId =>
                reports?.some(report => report.id === reportId)
              ) ?? []
          }))
        )
      }
    }
    for (const recipient of networkNormalizedRecipients) {
      if (
        recipient.attachments.some(reportId => !reports?.some(report => report.id === reportId))
      ) {
        setFieldValue(
          'networkNormalizedRecipients' as keyof FormValues,
          networkNormalizedRecipients.map(recipient => ({
            ...recipient,
            attachments:
              recipient.attachments.filter(reportId =>
                reports?.some(report => report.id === reportId)
              ) ?? []
          }))
        )
      }
    }
  }, [normalizedRecipients, networkNormalizedRecipients, reports, setFieldValue])

  function callback(i: number): void {
    if (Array.isArray(reports)) {
      setFieldValue(
        'reports',
        reports.filter((_config, index) => index !== i)
      )
    }
  }

  function onDropdownClick(item: NotificationRecipient, isNetwork: boolean): void {
    if (isNetwork) {
      form.setFieldsValue({
        networkNotificationRecipients: networkNormalizedRecipients.filter(
          recipient => recipient.id !== item.id
        )
      })
    } else {
      form.setFieldsValue({
        notificationRecipients: normalizedRecipients.filter(recipient => recipient.id !== item.id)
      })
    }
  }

  function sortReports(a: ReportConfig, b: ReportConfig): number {
    return levelMapper[a.type] - levelMapper[b.type]
  }

  return (
    <Form
      form={form}
      layout='horizontal'
      // className='bg-red-400'
      colon={false}
      labelCol={{
        className: 'font-semibold',
        span: 8
      }}
      wrapperCol={{
        span: 16
      }}
    >
      <Collapse
        defaultActiveKey={['1']}
        ghost
        className='flex flex-col'
        expandIcon={({ isActive }) => (
          <CaretRightOutlined
            className={isActive ? 'opacity-75' : 'opacity-50'}
            rotate={isActive ? 90 : 0}
          />
        )}
      >
        <Collapse.Panel key='1' header={<h3 className='font-semibold'>Schedule</h3>}>
          <Row className='w-full' gutter={32}>
            <Col span={12}>
              <Form.Item
                name='isOnHold'
                label='On Hold'
                valuePropName='checked'
                tooltip='The group won’t be sent despite the specified schedule'
                // labelCol={{
                //   className: 'font-semibold pt-0.5',
                //   span: 8,
                //   xl: {
                //     span: 4
                //   }
                // }}
                // wrapperCol={{
                //   span: 16,
                //   xl: {
                //     span: 20
                //   }
                // }}
              >
                <Switch
                  checkedChildren='On'
                  unCheckedChildren='Off'
                  className='-translate-y-[2px]'
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name='isExpired'
                label='Expired'
                valuePropName='checked'
                tooltip='The group won’t be sent despite the specified schedule either'
                // labelCol={{
                //   className: 'font-semibold pt-0.5',
                //   span: 8,
                //   xl: {
                //     span: 4
                //   }
                // }}
                // wrapperCol={{
                //   span: 16,
                //   xl: {
                //     span: 20
                //   }
                // }}
              >
                <Switch
                  checkedChildren='On'
                  unCheckedChildren='Off'
                  className='-translate-y-[2px]'
                />
              </Form.Item>
            </Col>
          </Row>
          <Row className='w-full' gutter={32}>
            <Col span={12}>
              <Form.Item
                name='effectiveDate'
                label='Effective Date'
                tooltip='The day the client starts with Lucent'
              >
                <DatePicker
                  size='large'
                  className='w-full rounded-md'
                  placeholder='Select Effective Date'
                  format='MM/DD/YYYY'
                />
              </Form.Item>

              <Form.Item
                name='terminationDate'
                label='Termination Date'
                tooltip='The day the client will cease active use of Lucent services. The selected day will be the last day of processing.'
              >
                <DatePicker
                  size='large'
                  className='w-full rounded-md'
                  placeholder='Select Termination Date'
                  format='MM/DD/YYYY'
                />
              </Form.Item>

              <Form.Item name='funding' label='Funding'>
                <Input
                  size='large'
                  className='w-full rounded-md'
                  placeholder='E.g. Lucent Treasury Ledger'
                />
              </Form.Item>

              <Form.Item name='accountExecutive' label='Account Executive'>
                <Input
                  size='large'
                  className='w-full rounded-md'
                  placeholder='E.g. John Appleseed'
                />
              </Form.Item>

              <Form.Item name='salesExecutive' label='Sales Executive'>
                <Input size='large' className='w-full rounded-md' placeholder='E.g. Jane Pearson' />
              </Form.Item>

              <Form.Item
                name='schedule'
                label='Schedule'
                tooltip='Days when the group will be sent'
              >
                <Dropdown
                  trigger={['click']}
                  dropdownRender={() => (
                    <ScheduleDropdown
                      value={schedule}
                      onChange={value => {
                        form.setFieldValue('schedule', value)
                      }}
                    />
                  )}
                >
                  <Button size='large' className='w-full bg-white rounded-md'>
                    <div className='flex justify-between items-center'>
                      <span
                        className={classNames(
                          'overflow-hidden w-full text-left overflow-ellipsis whitespace-nowrap',
                          {
                            'text-gray-300': isEmptySchedule(schedule)
                          }
                        )}
                      >
                        {schedule && !isEmptySchedule(schedule)
                          ? humanReadableSchedule(schedule)
                          : 'Select Schedule'}
                      </span>
                      <CalendarOutlined className='text-gray-400 text-sm translate-x-1 -translate-y-0.5' />
                    </div>
                  </Button>
                </Dropdown>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name='renewalDate'
                label='Renewal Date'
                tooltip='The day the current cycle starts with Lucent. The same as Effective Date if the client is new.'
              >
                <DatePicker
                  size='large'
                  className='w-full rounded-md'
                  placeholder='Select Renewal Date'
                  format='MM/DD/YYYY'
                />
              </Form.Item>

              <Form.Item
                name='runOutDate'
                label='Run Out Date'
                tooltip='The day after which we stop processing claim information. The selected day will be the last day of processing.'
              >
                <DatePicker
                  size='large'
                  className='w-full rounded-md'
                  placeholder='Select Run Out Date'
                  format='MM/DD/YYYY'
                />
              </Form.Item>

              <Form.Item name='rxCompany' label='RX Company'>
                <Input
                  size='large'
                  className='w-full rounded-md'
                  placeholder='E.g. Express Scripts'
                />
              </Form.Item>

              <Form.Item name='accountManager' label='Account Manager'>
                <Input
                  size='large'
                  className='w-full rounded-md'
                  placeholder='E.g. Michael Scott Jr.'
                />
              </Form.Item>

              <Form.Item name='groupContact' label='Group Contact'>
                <Input
                  size='large'
                  className='w-full rounded-md'
                  placeholder='E.g. Dwight Schrute'
                />
              </Form.Item>

              <Row className='w-full'>
                <Col span={12}>
                  <Form.Item
                    name='requestForFunding'
                    valuePropName='checked'
                    label='RFF'
                    labelCol={{
                      className: 'font-semibold',
                      span: 16
                    }}
                  >
                    <Checkbox />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item name='release' valuePropName='checked' label='Release'>
                    <Checkbox />
                  </Form.Item>
                </Col>
              </Row>
            </Col>
          </Row>

          <Row className='w-full' gutter={64}>
            <Col span={24}>
              {Array.isArray(availableCoverageCategories) &&
                availableCoverageCategories.length > 0 && (
                  <>
                    <Form.Item
                      name='coverageCategories'
                      label='Coverage Categories'
                      labelCol={{
                        className: 'font-semibold pt-0.5',
                        span: 8,
                        xl: {
                          span: 4
                        }
                      }}
                      wrapperCol={{
                        span: 16,
                        xl: {
                          span: 20
                        }
                      }}
                    >
                      <Checkbox.Group
                        options={
                          [...availableCoverageCategories]
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map(category => ({
                              label: category.name,
                              value: category.id
                            })) ?? []
                        }
                      />
                    </Form.Item>

                    {locationKey === '001' && specialCaseCoverageCategories.length > 0 && (
                      <Row>
                        <Col
                          span={16}
                          offset={8}
                          xl={{
                            span: 20,
                            offset: 4
                          }}
                        >
                          <p className='-mt-6 mb-3 text-gray-500'>
                            In the schedule, 1 day will be added to the Paid Through date for the{' '}
                            {humanJoin(specialCaseCoverageCategories)}{' '}
                            {pluralize(
                              specialCaseCoverageCategories.length,
                              'coverage category',
                              'coverage categories'
                            )}
                            .
                          </p>
                        </Col>
                      </Row>
                    )}

                    {paidThrough.length > 0 && (
                      <Form.Item
                        label='Paid Through'
                        labelCol={{
                          className: 'font-semibold pt-0.5',
                          span: 8,
                          xl: {
                            span: 4
                          }
                        }}
                        wrapperCol={{
                          span: 16,
                          xl: {
                            span: 20
                          }
                        }}
                      >
                        {paidThrough.map(category => (
                          <Tag key={category.id} color='blue' className='min-w-[3rem] text-center'>
                            {`${category.name}: ${category.paid_through}`}
                          </Tag>
                        ))}
                      </Form.Item>
                    )}
                  </>
                )}

              {(!Array.isArray(availableCoverageCategories) ||
                availableCoverageCategories.length === 0) && (
                <Form.Item
                  name='coverageCategories'
                  label='Coverage Categories'
                  labelCol={{
                    className: 'font-semibold pt-0.5',
                    span: 8,
                    xl: {
                      span: 4
                    }
                  }}
                  wrapperCol={{
                    span: 16,
                    xl: {
                      span: 20
                    }
                  }}
                >
                  <span className='text-gray-500'>No coverage categories available</span>
                </Form.Item>
              )}

              <Form.Item
                name='notes'
                label='Notes'
                className='textarea'
                labelCol={{
                  className: 'font-semibold pt-0.5',
                  span: 8,
                  xl: {
                    span: 4
                  }
                }}
                wrapperCol={{
                  span: 16,
                  xl: {
                    span: 20
                  }
                }}
              >
                <Input.TextArea
                  size='large'
                  className='w-full rounded-md'
                  placeholder='You can add additional notes here'
                />
              </Form.Item>
            </Col>
          </Row>

          <Row className='w-full' gutter={64}>
            <Col span={12} className='relative'>
              <Form.Item
                name='releaseType'
                label='Release Type'
                labelCol={{
                  className: 'font-semibold',
                  span: 8
                }}
              >
                <Radio.Group className='flex flex-col gap-2'>
                  <Radio value='auto'>Auto</Radio>
                  <Radio value='email'>Email</Radio>
                  <Radio value='deposit'>Deposit</Radio>
                  <Radio value='level-funded'>Level Funded</Radio>
                </Radio.Group>
              </Form.Item>

              <Form.Item
                name='releaseDay'
                hidden={releaseType !== 'auto'}
                required={releaseType === 'auto'}
                className='absolute top-0 right-0 w-60'
              >
                <Select placeholder='Select Day' className='' size='large' allowClear>
                  <Select.Option value={1}>Monday</Select.Option>
                  <Select.Option value={2}>Tuesday</Select.Option>
                  <Select.Option value={3}>Wednesday</Select.Option>
                  <Select.Option value={4}>Thursday</Select.Option>
                  <Select.Option value={5}>Friday</Select.Option>
                </Select>
              </Form.Item>
            </Col>

            <Col span={12} className='items-end'>
              <Form.Item
                name='ignoreStopLoss'
                label='Stop Loss'
                labelCol={{
                  className: 'font-semibold',
                  span: 8
                }}
                wrapperCol={{
                  className: 'pl-2'
                }}
                valuePropName='checked'
              >
                <Checkbox>Ignore Stop Loss flag and pull the money</Checkbox>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name='reports' noStyle hidden>
            <Input />
          </Form.Item>
          <Form.Item name='fundingType' noStyle hidden>
            <Input />
          </Form.Item>
          <Form.Item name='network' noStyle hidden>
            <Input />
          </Form.Item>
          <Form.Item name='notificationRecipients' noStyle hidden>
            <Input />
          </Form.Item>
          <Form.Item name='networkNotificationRecipients' noStyle hidden>
            <Input />
          </Form.Item>
          <Form.Item name='coverageCategories' noStyle hidden>
            <Input />
          </Form.Item>
        </Collapse.Panel>

        <Collapse.Panel
          forceRender
          key='2'
          header={<h3 className='font-semibold'>Check Register Reports</h3>}
        >
          <Form.Item
            shouldUpdate={(prev: FormValues, current) => prev.reports !== current.reports}
            noStyle
          >
            {({ getFieldValue }) => {
              const reports: ReportConfig[] = (
                (getFieldValue('reports') as ReportConfig[]) ?? []
              ).sort(sortReports)

              return (
                <div className='flex gap-[2.65rem] pb-6 items-baseline'>
                  <div className='flex flex-col'>
                    <label className='inline-flex items-baseline font-semibold'>
                      Selected
                      <Tooltip title='Selected Check Register types will be sent to EBIX and will also be available in the attachments dropdown'>
                        <InfoCircleOutlined
                          className='ml-2 -translate-y-0.5'
                          style={{ color: 'rgba(0, 0, 0, .45)' }}
                        />
                      </Tooltip>
                    </label>
                  </div>

                  <div className='flex flex-col'>
                    <div className='flex flex-wrap gap-1 gap-y-3'>
                      {reports.length === 0 && (
                        <p className='text-gray-500 pb-2 pt-1.5 mt-0'>
                          No report types selected yet
                        </p>
                      )}

                      {reports.map((report, i) => (
                        <Tag
                          key={i}
                          closable={
                            !(
                              [
                                CRReportType.Summary,
                                CRReportType.NetworkSummary,
                                CRReportType.NetworkDetail
                              ].includes(report.type) ||
                              (report.type === CRReportType.Detail &&
                                !report.options.includeDiagnosisDetails &&
                                report.options.includePHI)
                            )
                          }
                          onClose={event => {
                            event.preventDefault()

                            const hasDependentRecipients = normalizedRecipients.some(recipient =>
                              recipient.attachments.includes(report.id)
                            )

                            if (!hasDependentRecipients) {
                              callback(i)
                              return
                            }

                            Modal.confirm({
                              title: 'Are you sure you want to remove this report type?',
                              content:
                                'Some recipients are configured to receive this report type. Removing it will also remove the corresponding attachments from some of the recipients.',
                              onOk() {
                                callback(i)

                                notification.warning({
                                  message: 'Recipients configs updated',
                                  description:
                                    'Some attachments were removed because they are no longer available due to the report type changes.'
                                })
                              }
                            })
                          }}
                          className='text-base px-3 py-1 flex items-center [&>.ant-tag-close-icon]:ml-2 [&>.ant-tag-close-icon]:translate-y-[1px]'
                        >
                          {getReportConfigLabel(report)}
                        </Tag>
                      ))}
                    </div>
                  </div>
                </div>
              )
            }}
          </Form.Item>

          <div
            className={classNames('flex gap-4 items-center', {
              'pt-1.5': addReportTypeOptions.length === 0
            })}
          >
            <label className='inline-flex items-baseline font-semibold'>Add report type</label>

            {addReportTypeOptions.length === 0 && (
              <p className='text-gray-500'>All report types have already been added</p>
            )}

            {addReportTypeOptions.length > 0 && (
              <>
                <Select
                  options={addReportTypeOptions}
                  placeholder='Select report type'
                  className='w-48'
                  size='large'
                  value={addReportTypeForm.type}
                  onChange={value => {
                    setAddReportTypeForm(
                      form =>
                        ({
                          ...form,
                          type: value,
                          options: [CRReportType.Detail, CRReportType.NetworkDetail].includes(value)
                            ? {
                                includeDiagnosisDetails: false,
                                includePHI: true
                              }
                            : undefined
                        }) as unknown as AddReportTypeFormValues
                    )
                  }}
                />

                <Tooltip
                  title={
                    selectedReportConfigHasAlreadyBeenAdded
                      ? 'This report type has already been added'
                      : undefined
                  }
                >
                  <div>
                    <Button
                      htmlType='button'
                      icon={<PlusOutlined />}
                      className='flex justify-center items-center'
                      size='large'
                      disabled={
                        addReportTypeForm.type == null || selectedReportConfigHasAlreadyBeenAdded
                      }
                      onClick={event => {
                        event.preventDefault()

                        form.setFieldValue('reports', [
                          ...((form.getFieldValue('reports') as ReportConfig[]) ?? []),
                          {
                            id: uuid(),
                            ...addReportTypeForm
                          }
                        ])

                        setAddReportTypeForm({
                          type: undefined,
                          options: undefined
                        } as unknown as AddReportTypeFormValues)
                      }}
                    >
                      Add
                    </Button>
                  </div>
                </Tooltip>
              </>
            )}
          </div>

          {addReportTypeForm.type === CRReportType.Detail && (
            <div className='flex flex-col pt-3 pl-[7.725rem] gap-2'>
              <label className='flex gap-2 items-baseline'>
                <Checkbox
                  checked={addReportTypeForm.options.includeDiagnosisDetails}
                  onChange={event => {
                    setAddReportTypeForm(form => ({
                      ...form,
                      options: {
                        ...(form as AddDetailReportTypeFormValues).options,
                        includeDiagnosisDetails: event.target.checked
                      }
                    }))
                  }}
                />
                Include Diagnosis Details
              </label>

              <label className='flex gap-2 items-baseline'>
                <Checkbox
                  checked={!addReportTypeForm.options.includePHI}
                  onChange={event => {
                    setAddReportTypeForm(form => ({
                      ...form,
                      options: {
                        ...(form as AddDetailReportTypeFormValues).options,
                        includePHI: !event.target.checked
                      }
                    }))
                  }}
                />
                Hide PHI
              </label>
            </div>
          )}
        </Collapse.Panel>

        <Collapse.Panel
          key='3'
          forceRender
          header={<h3 className='font-semibold'>Group Email Management</h3>}
        >
          <Row gutter={[24, 0]}>
            <Col span={10}>
              <div className='inline-flex items-baseline'>
                <span className='font-semibold'>Email</span>
              </div>
            </Col>
            <Col span={8}>
              <div className='inline-flex items-baseline'>
                <span className='font-semibold'>Attachments</span>

                <Tooltip title='Selected report types will be attached to the email notification'>
                  <InfoCircleOutlined
                    className='ml-2 -translate-y-[1px]'
                    style={{ color: 'rgba(0, 0, 0, .45)' }}
                  />
                </Tooltip>
              </div>
            </Col>
            <Col span={4}>
              <div className='inline-flex items-baseline'>
                <span className='font-semibold'>Delayed Notice</span>

                <Tooltip title='Notification email will be sent on the next day at 6 AM CST'>
                  <InfoCircleOutlined
                    className='ml-2 -translate-y-[1px]'
                    style={{ color: 'rgba(0, 0, 0, .45)' }}
                  />
                </Tooltip>
              </div>
            </Col>
          </Row>

          {normalizedRecipients.length === 0 && (
            <Row className='pt-4'>
              <Col span={20}>
                <span className='text-gray-500'>No recipients yet</span>
              </Col>
            </Row>
          )}

          {normalizedRecipients.map((item, i) => {
            return (
              <Row key={item.id} className={classNames({ 'pt-6': i === 0 })} gutter={[24, 0]}>
                <Col span={10}>
                  <Form.Item
                    name={['notificationRecipients', i, 'email']}
                    labelCol={{ span: 0 }}
                    wrapperCol={{ span: 24 }}
                  >
                    <Input
                      size='large'
                      className='w-full rounded-md'
                      placeholder='E.g. mr.anderson@lucenthealth.com'
                    />
                  </Form.Item>
                </Col>

                <Col span={8}>
                  <Form.Item
                    name={['notificationRecipients', i, 'attachments']}
                    labelCol={{ span: 0 }}
                    wrapperCol={{ span: 24 }}
                  >
                    <Select
                      allowClear
                      mode='multiple'
                      size='large'
                      className='w-full rounded-md'
                      placeholder='E.g. Summary'
                      disabled={!reports?.length}
                      options={
                        reports
                          ?.filter(r =>
                            [
                              CRReportType.Detail,
                              CRReportType.Summary,
                              CRReportType.OnePage,
                              CRReportType.Winsupply
                            ].includes(r.type)
                          )
                          .map(config => ({
                            label: getReportConfigLabel(config),
                            value: config.id
                          })) ?? []
                      }
                    />
                  </Form.Item>
                </Col>

                <Col span={4}>
                  <Form.Item
                    name={['notificationRecipients', i, 'delayed_notice']}
                    labelCol={{ span: 0 }}
                    wrapperCol={{ span: 24 }}
                    valuePropName='checked'
                  >
                    <Checkbox />
                  </Form.Item>
                </Col>

                <Col span={2}>
                  <Dropdown
                    menu={{
                      items: [
                        {
                          key: '1',
                          label: 'Remove',
                          icon: <DeleteOutlined />,
                          danger: true,
                          onClick() {
                            onDropdownClick(item, false)
                            Modal.confirm({
                              title: 'Are you sure you want to remove this recipient?',
                              content: 'This action cannot be undone.',
                              onOk() {
                                onDropdownClick(item, false)
                              }
                            })
                          }
                        }
                      ]
                    }}
                  >
                    <Button
                      htmlType='button'
                      icon={<EllipsisOutlined />}
                      shape='circle'
                      type='text'
                    />
                  </Dropdown>
                </Col>
              </Row>
            )
          })}

          <Row
            gutter={[24, 0]}
            className={classNames({
              'pt-4': !(recipients?.length ?? 0)
            })}
          >
            <Col span={20}>
              <Button
                icon={<PlusOutlined className='' />}
                htmlType='button'
                className='flex items-center'
                onClick={() => {
                  const item: NotificationRecipient = {
                    id: uuid(),
                    email: '',
                    attachments: [],
                    delayed_notice: false,
                    is_network: false
                  }

                  form.setFieldValue('notificationRecipients', [...normalizedRecipients, item])
                }}
              >
                Add Recipient
              </Button>
            </Col>
          </Row>
        </Collapse.Panel>

        <Collapse.Panel
          key='4'
          forceRender
          collapsible={network !== 'none' ? 'header' : 'disabled'}
          header={<h3 className='font-semibold'>Network Email Management</h3>}
        >
          <Row gutter={[24, 0]}>
            <Col span={10}>
              <div className='inline-flex items-baseline'>
                <span className='font-semibold'>Email</span>
              </div>
            </Col>
            <Col span={8}>
              <div className='inline-flex items-baseline'>
                <span className='font-semibold'>Attachments</span>

                <Tooltip title='Selected report types will be attached to the email notification'>
                  <InfoCircleOutlined
                    className='ml-2 -translate-y-[1px]'
                    style={{ color: 'rgba(0, 0, 0, .45)' }}
                  />
                </Tooltip>
              </div>
            </Col>
            <Col span={4}>
              <div className='inline-flex items-baseline'>
                <span className='font-semibold'>Delayed Notice</span>

                <Tooltip title='Notification email will be sent on the next day at 6 AM CST'>
                  <InfoCircleOutlined
                    className='ml-2 -translate-y-[1px]'
                    style={{ color: 'rgba(0, 0, 0, .45)' }}
                  />
                </Tooltip>
              </div>
            </Col>
          </Row>

          {networkNormalizedRecipients.length === 0 && (
            <Row className='pt-4'>
              <Col span={20}>
                <span className='text-gray-500'>No recipients yet</span>
              </Col>
            </Row>
          )}

          {networkNormalizedRecipients.map((item, i) => {
            return (
              <Row key={item.id} className={classNames({ 'pt-6': i === 0 })} gutter={[24, 0]}>
                <Col span={10}>
                  <Form.Item
                    name={['networkNotificationRecipients', i, 'email']}
                    labelCol={{ span: 0 }}
                    wrapperCol={{ span: 24 }}
                  >
                    <Input
                      size='large'
                      className='w-full rounded-md'
                      placeholder='E.g. mr.anderson@lucenthealth.com'
                      disabled={network === 'none'}
                    />
                  </Form.Item>
                </Col>

                <Col span={8}>
                  <Form.Item
                    name={['networkNotificationRecipients', i, 'attachments']}
                    labelCol={{ span: 0 }}
                    wrapperCol={{ span: 24 }}
                  >
                    <Select
                      allowClear
                      mode='multiple'
                      size='large'
                      className='w-full rounded-md'
                      placeholder='E.g. Summary'
                      disabled={!reports?.length || network === 'none'}
                      options={
                        reports
                          ?.filter(r =>
                            [CRReportType.NetworkDetail, CRReportType.NetworkSummary].includes(
                              r.type
                            )
                          )
                          .map(config => ({
                            label: getReportConfigLabel(config),
                            value: config.id
                          })) ?? []
                      }
                    />
                  </Form.Item>
                </Col>

                <Col span={4}>
                  <Form.Item
                    name={['networkNotificationRecipients', i, 'delayed_notice']}
                    labelCol={{ span: 0 }}
                    wrapperCol={{ span: 24 }}
                    valuePropName='checked'
                  >
                    <Checkbox disabled={network === 'none'} />
                  </Form.Item>
                </Col>

                <Col span={2}>
                  <Dropdown
                    disabled={network === 'none'}
                    menu={{
                      items: [
                        {
                          key: '1',
                          label: 'Remove',
                          icon: <DeleteOutlined />,
                          danger: true,
                          onClick() {
                            onDropdownClick(item, true)
                            Modal.confirm({
                              title: 'Are you sure you want to remove this recipient?',
                              content: 'This action cannot be undone.',
                              onOk() {
                                onDropdownClick(item, true)
                              }
                            })
                          }
                        }
                      ]
                    }}
                  >
                    <Button
                      htmlType='button'
                      disabled={network === 'none'}
                      icon={<EllipsisOutlined />}
                      shape='circle'
                      type='text'
                    />
                  </Dropdown>
                </Col>
              </Row>
            )
          })}

          <Row
            gutter={[24, 0]}
            className={classNames({
              'pt-4': !(recipients?.length ?? 0)
            })}
          >
            <Col span={20}>
              <Button
                icon={<PlusOutlined className='' />}
                htmlType='button'
                disabled={network === 'none'}
                className='flex items-center'
                onClick={() => {
                  const item: NotificationRecipient = {
                    id: uuid(),
                    email: '',
                    attachments: [],
                    delayed_notice: false,
                    is_network: true
                  }

                  form.setFieldValue('networkNotificationRecipients', [
                    ...networkNormalizedRecipients,
                    item
                  ])
                }}
              >
                Add Recipient
              </Button>
            </Col>
          </Row>
        </Collapse.Panel>
      </Collapse>

      <Row className='px-4 pb-4'>
        <Col span={24} className='flex justify-end'>
          <Space>
            <Button
              size='large'
              type='link'
              htmlType='reset'
              className='rounded-md'
              onClick={event => {
                event.preventDefault()

                check(onCancel)
              }}
            >
              Cancel
            </Button>

            <Tooltip title={isDirty ? undefined : 'The form has not been changed yet'}>
              <Button
                size='large'
                type='primary'
                htmlType='submit'
                loading={isSubmitting}
                onClick={event => {
                  event.preventDefault()
                  onSave()
                }}
                disabled={!isDirty}
              >
                Save
              </Button>
            </Tooltip>
          </Space>
        </Col>
      </Row>
    </Form>
  )
}
